import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { t } from '../../../../services/i18nService';
import AlternativesMenu from './AlternativesMenu';
import styles from './inlineChoiceInteraction.scss';
import responseService from '../../../../services/ResponseService';
import stringUtils from '../../../../qti/utils/StringUtils';
import scorableItemNumberingService from '../../../../services/questionView/ScorableItemNumberingService';
import constants from '../../../../qti/Constants';
import InlineChoiceInteraction from './InlineChoiceInteraction';

const HorizontalSelectList = ({
    alternatives,
    value,
    focusElement,
    handleAlternativeBoxClick,
    questionProps,
}) => {
    const {
        callback,
        model,
        isReadOnly,
        questionModel,
        currentScorableItem,
        flaggedItems,
        correctResponses,
    } = questionProps;
    const labelMessage = t('interactions.inlineChoice.fieldWrapperAriaLabel');
    const instanceId = stringUtils.getUID();
    const isAttempted = value !== undefined && value !== '';
    const interactionNumber = scorableItemNumberingService.getInteractionNumber(questionModel.contentRevisionId, model.identifier);
    const isItemPreSelected = currentScorableItem === Number(interactionNumber);
    const { hasGranularNumbering } = model;
    const isFlagged = flaggedItems && flaggedItems.includes(`${questionModel.contentRevisionId}-${interactionNumber}`);
    const isPDFView = InlineChoiceInteraction.shouldShowAlternativesInfo(isReadOnly, correctResponses);

    const [isChoiceInteractionMenuOpen, setIsChoiceInteractionMenuOpen] = useState(false);
    const [classes, setClasses] = useState([styles.inlineChoiceInteraction]);

    const inlineChoiceInteractionRef = useRef(null);

    useEffect(() => {
        const menuStyles = isChoiceInteractionMenuOpen
            ? [styles.inlineChoiceInteractionActive]
            : [styles.inlineChoiceInteraction];

        setClasses(menuStyles);

        if (isFlagged && hasGranularNumbering) {
            setClasses(prev => [...prev, 'flagged']);
        }
    }, [isChoiceInteractionMenuOpen, isFlagged]);

    const handleToggleMenu = () => {
        handleAlternativeBoxClick();
        setIsChoiceInteractionMenuOpen(!isChoiceInteractionMenuOpen);
    };

    const getAlternativesContentById = (alternativeId) => {
        if (alternativeId === constants.noChoiceMadeOption) {
            return t('interactions.selectAlternative');
        }

        return alternatives.find(({ identifier }) => identifier === alternativeId).content;
    };

    const buttonContent = isAttempted ? getAlternativesContentById(value) : t('interactions.selectAlternative');
    const sortedByContent = responseService.getLongestAlternative(alternatives);

    // calculate max width of the option depends on the font-size
    const maxWidthOption = (sortedByContent?.content.length > t('interactions.selectAlternative')
        ? sortedByContent?.content.length
        : t('interactions.selectAlternative')).length * 8;

    let buttonText = '';
    if (isAttempted && buttonContent !== t('interactions.selectAlternative')) {
        buttonText = buttonContent;
    } else if (!isPDFView) {
        buttonText = interactionNumber;
    }

    return (
        <span className={styles.inlineChoiceInteractionWrapper}>
            <button
                style={{ minWidth: `${maxWidthOption}px` }}
                id={instanceId}
                ref={(element) => {
                    inlineChoiceInteractionRef.current = element;
                    if (hasGranularNumbering) {
                        focusElement(isItemPreSelected, element);
                    }
                }}
                onClick={handleToggleMenu}
                className={classes.join(' ')}
                disabled={isReadOnly}
                name={instanceId}
            >
                <span className={styles.visuallyHidden}>{labelMessage}: </span>
                { hasGranularNumbering ? buttonText : buttonContent }

                { isChoiceInteractionMenuOpen &&
                    <AlternativesMenu
                        alternatives={alternatives}
                        value={value}
                        model={model}
                        callback={callback}
                        inlineChoiceInteractionRef={inlineChoiceInteractionRef}
                        setIsChoiceInteractionMenuOpen={setIsChoiceInteractionMenuOpen}
                    />
                }
            </button>
        </span>
    );
};

HorizontalSelectList.propTypes = {
    alternatives: PropTypes.array.isRequired,
    value: PropTypes.string,
    focusElement: PropTypes.func.isRequired,
    handleAlternativeBoxClick: PropTypes.func.isRequired,
    questionProps: PropTypes.object.isRequired,
};

export default HorizontalSelectList;
